/**
 * Minimal markdown → React renderer. Just enough for our help articles
 * (headings, bold, italics, lists, code spans, paragraphs, links, simple
 * tables). Keeps us off another dep and gives full control over styling.
 *
 * Not safe for untrusted markdown — articles are authored by us, so this
 * is fine.
 */
import React from 'react';

const renderInline = (text) => {
  // Order matters — handle inline code first so other patterns don't
  // double-render inside backticks.
  const parts = [];
  let remaining = text;
  let key = 0;

  const patterns = [
    { re: /`([^`]+)`/, render: (m) => <code key={key++} className="px-1 py-0.5 rounded bg-muted font-mono text-[0.85em]">{m[1]}</code> },
    { re: /\*\*([^*]+)\*\*/, render: (m) => <strong key={key++}>{m[1]}</strong> },
    { re: /\*([^*]+)\*/, render: (m) => <em key={key++}>{m[1]}</em> },
    { re: /\[([^\]]+)\]\(([^)]+)\)/, render: (m) => <a key={key++} href={m[2]} className="text-primary underline hover:no-underline" target="_blank" rel="noreferrer">{m[1]}</a> },
  ];

  // Greedy multi-pattern scanner.
  while (remaining.length > 0) {
    let earliest = null;
    for (const p of patterns) {
      const m = remaining.match(p.re);
      if (m && (earliest === null || m.index < earliest.index)) {
        earliest = { ...p, match: m, index: m.index };
      }
    }
    if (!earliest) {
      parts.push(remaining);
      break;
    }
    if (earliest.index > 0) parts.push(remaining.slice(0, earliest.index));
    parts.push(earliest.render(earliest.match));
    remaining = remaining.slice(earliest.index + earliest.match[0].length);
  }
  return parts;
};

export const Markdown = ({ source, className = '' }) => {
  if (!source) return null;
  const lines = source.split('\n');
  const blocks = [];
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // Heading
    const hMatch = trimmed.match(/^(#{1,4})\s+(.*)$/);
    if (hMatch) {
      const level = hMatch[1].length;
      const HeadingTag = `h${level}`;
      const sizes = ['text-3xl mt-8 mb-4', 'text-2xl mt-6 mb-3', 'text-xl mt-5 mb-2', 'text-lg mt-4 mb-2'];
      blocks.push(
        React.createElement(
          HeadingTag,
          { key: key++, className: `font-bold ${sizes[level - 1]}` },
          renderInline(hMatch[2])
        )
      );
      i++;
      continue;
    }

    // Blockquote
    if (trimmed.startsWith('> ')) {
      const text = trimmed.replace(/^>\s+/, '');
      blocks.push(<blockquote key={key++} className="border-l-4 border-primary/40 bg-secondary/40 pl-4 py-2 my-3 italic">{renderInline(text)}</blockquote>);
      i++;
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(trimmed) || /^- \[[ x]\]\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^[-*]\s+/, '');
        const checkbox = itemText.match(/^\[([ x])\]\s+(.*)$/);
        if (checkbox) {
          items.push(
            <li key={key++} className="flex items-start gap-2">
              <span className="mt-1">{checkbox[1] === 'x' ? '☑' : '☐'}</span>
              <span>{renderInline(checkbox[2])}</span>
            </li>
          );
        } else {
          items.push(<li key={key++}>{renderInline(itemText)}</li>);
        }
        i++;
      }
      blocks.push(<ul key={key++} className="list-disc pl-6 my-3 space-y-1.5 marker:text-primary">{items}</ul>);
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(trimmed)) {
      const items = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i].trim())) {
        const itemText = lines[i].trim().replace(/^\d+\.\s+/, '');
        items.push(<li key={key++}>{renderInline(itemText)}</li>);
        i++;
      }
      blocks.push(<ol key={key++} className="list-decimal pl-6 my-3 space-y-1.5 marker:text-primary marker:font-semibold">{items}</ol>);
      continue;
    }

    // Table (very simple — | a | b |\n|--|--|)
    if (/^\|.+\|$/.test(trimmed) && i + 1 < lines.length && /^\|[\s\-|]+\|$/.test(lines[i + 1].trim())) {
      const header = trimmed.slice(1, -1).split('|').map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && /^\|.+\|$/.test(lines[i].trim())) {
        rows.push(lines[i].trim().slice(1, -1).split('|').map((c) => c.trim()));
        i++;
      }
      blocks.push(
        <div key={key++} className="my-4 overflow-x-auto border border-border rounded-md">
          <table className="w-full text-sm">
            <thead className="bg-secondary/40 text-foreground/90">
              <tr>{header.map((h, idx) => <th key={idx} className="px-3 py-2 text-left font-semibold">{renderInline(h)}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={idx} className="border-t border-border">
                  {r.map((c, cidx) => <td key={cidx} className="px-3 py-2 align-top">{renderInline(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }

    // Blank line
    if (trimmed === '') {
      i++;
      continue;
    }

    // Paragraph (collect consecutive non-blank lines as one paragraph)
    const paraLines = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].trim().match(/^(#{1,4}\s|> |[-*]\s|\d+\.\s|\|)/)
    ) {
      paraLines.push(lines[i].trim());
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push(<p key={key++} className="my-3 leading-relaxed">{renderInline(paraLines.join(' '))}</p>);
    }
  }

  return <div className={`prose-equipr ${className}`}>{blocks}</div>;
};

export default Markdown;
